import { useMemo, useState, useCallback } from "react";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Checkbox from "@mui/material/Checkbox";

import StatusBadge from "cxfcomponents/StatusBadge";
import DataTable from "cxfcomponents/Tables/DataTable";

import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";

import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";

const SHARE_ACCESS_TYPE_OPTIONS = [
  {
    value: "readOnly",
    label: "Read Only"
  },
  {
    value: "editor",
    label: "Editor"
  }
];

const toggleUserSelection = (selectedUsers, user_id, checked, isShareMode) => {
  if (checked) {
    return isShareMode
      ? [...selectedUsers, { shared_user_id: user_id, access_type: "readOnly" }]
      : [...selectedUsers, { shared_user_id: user_id }];
  }

  return selectedUsers.filter((selectedUser) => selectedUser.shared_user_id !== user_id);
};

const ShareUnshareDialog = ({
  open,
  isShareMode,
  onClose,
  onAccept,
  shareUnshareItem,
  shareUnshareItemName,
  users,
  handleOpenExternalUsersShareDialog
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectCheckbox = useCallback(
    (event, user_id) => {
      const { checked } = event.target;

      const updateSelectedUsers = toggleUserSelection(selectedUsers, user_id, checked, isShareMode);
      setSelectedUsers(updateSelectedUsers);
    },
    [selectedUsers, isShareMode]
  );

  const handleShare = () => {
    onAccept(selectedUsers, setSelectedUsers);
  };

  const isDisabled = (user_id) => {
    return !selectedUsers.find((u) => u.shared_user_id === user_id);
  };

  const isSelected = (user_id) => {
    return Boolean(selectedUsers.find((u) => u.shared_user_id === user_id));
  };

  const getDefaultValue = (user_id) => {
    const user = selectedUsers.find((u) => u.shared_user_id === user_id);
    if (user) {
      return SHARE_ACCESS_TYPE_OPTIONS.find((option) => option.value === user.access_type);
    } else {
      return {
        value: "readOnly",
        label: "Read Only"
      };
    }
  };

  const handleShareTypeChange = (selectValue, user_id) => {
    setSelectedUsers((prev) =>
      prev.map((user) => {
        if (user.shared_user_id === user_id) {
          return {
            ...user,
            access_type: selectValue.value
          };
        }
        return user;
      })
    );
  };

  const handleCloseShareDialog = () => {
    setSelectedUsers([]);
    onClose();
  };

  const dataTableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "Share to",
          accessor: "share_to",
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => (
            <Checkbox checked={isSelected(value)} onClick={(e) => handleSelectCheckbox(e, value)} />
          )
        },
        {
          Header: "users",
          accessor: "users",
          width: 500,
          disableSortBy: false,
          canGroupBy: false,
          disableGlobalFilter: true,
          defaultSortOrder: "desc"
        },
        {
          Header: "status",
          accessor: "status",
          Cell: ({ value }) => {
            return <StatusBadge status={value} />;
          },
          canGroupBy: false,
          disableGlobalFilter: true
        },
        {
          Header: "Share type",
          accessor: "share_type",
          disableSortBy: true,
          show: isShareMode,
          Cell: ({ value }) => (
            <SoftBox width="250px" className="inline-select-wrapper">
              <SoftSelect
                defaultValue={getDefaultValue(value)}
                isDisabled={isDisabled(value)}
                options={SHARE_ACCESS_TYPE_OPTIONS}
                onChange={(selectValue) => handleShareTypeChange(selectValue, value)}
              />
            </SoftBox>
          )
        }
      ],
      rows: [
        ...(users?.map((user) => {
          return {
            share_to: user.auth0_id,
            users: (
              <ProductCellfromUser
                id={user.auth0_id}
                image={user.image}
                name={`${user.first_name} ${user.last_name}`}
                firstName={user.first_name}
                lastName={user.last_name}
                email={user.email_address}
                checked={true}
              />
            ),
            status: user?.status,
            share_type: user.auth0_id
          };
        }) || [])
      ]
    };
  }, [users, selectedUsers, handleSelectCheckbox, handleShareTypeChange, isShareMode]);

  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{
        style: {
          maxWidth: "1020px"
        }
      }}
    >
      <DialogTitle>
        {isShareMode ? "Share" : "Unshare"} {shareUnshareItem}: {shareUnshareItemName}
      </DialogTitle>
      <DialogContent>
        <DataTable table={dataTableData} entriesPerPage={{ defaultValue: 10 }} splitted={true} />
      </DialogContent>
      <DialogActions>
        <SoftButton
          sx={{ marginRight: "auto", display: "none" }}
          color="light"
          onClick={handleOpenExternalUsersShareDialog}
        >
          Share to external users
        </SoftButton>
        <SoftButton variant="outlined" color="info" onClick={handleCloseShareDialog}>
          Close
        </SoftButton>
        <SoftButton color="info" onClick={handleShare}>
          {isShareMode ? "Share" : "Unshare"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

export default ShareUnshareDialog;
